import debugFactory from "debug";
import { DateTime } from "luxon";
import { createRouter, createWebHistory } from "vue-router";
import storage from "@teaming/common-storage";

const debug = debugFactory( "teaming:teaming-web:router" );

const loadedSlices = new Map();
const intercomWidgetBlacklist = [
	"/onboarding",
];
/*
	Part of the convention/contract for slices is that each slice has a routes.js module,
	which defines the routes used by that slice, etc. Here is where we register those
	routes - and we do so without invoking the slice's main factory function yet (which is what
	a slice's index.js module exports). This approach allows us to keep a slice's routes co-located
	with the rest of the slice's assets, and also allows us to move slice-specific layout components
	into the slice package itself, rather than the awkward fence straddling of having layouts and
	routes for slices in the host app, while everything else lives in the slice package.
*/
const sliceRouteModules = [
	require( "@teaming/slice-actionItems/routes" ),
	require( "@teaming/slice-apple/routes" ),
	require( "@teaming/slice-gokr/routes" ),
	require( "@teaming/slice-onboarding3/routes" ),
	require( "@teaming/slice-settings/routes" ),
	require( "@teaming/slice-inbox/routes" ),
	// NOTE: slice-teamlike should be set up prior to slice-one-on-one and slice-team
	// due to its instant initialization
	require( "@teaming/slice-teamlike/routes" ),
	require( "@teaming/slice-one-on-one/routes" ),
	require( "@teaming/slice-team/routes" ),
];

export default function routerFactory( {
	app,
	authService,
	config,
	layouts,
	store,
	telemetry,
	i18n,
} ) {
	const routesFactoryArgs = {
		app,
		authService,
		config,
		layouts,
		loadedSlices,
		store,
		telemetry,
		i18n,
	};
	const sliceRoutes = sliceRouteModules.reduce( ( routes, sliceRouteFactory ) => {
		const factoryFn = typeof sliceRouteFactory.default === "function" ?
			sliceRouteFactory.default :
			sliceRouteFactory;
		routes.push( ...factoryFn( routesFactoryArgs ) );
		return routes;
	}, [] );

	const routes = [
		{
			path: "",
			name: "default",
			redirect: to => {
				const newPath = to.path.split( "/" ).slice( 0, 3 ).join( "/" ); // eslint-disable-line no-magic-numbers
				return { path: `${ newPath }one-on-ones`, };
			},
		},
		{
			path: "/logout",
			// eslint-disable-next-line no-unused-vars
			async beforeEnter( to, from, next ) {
				await authService.logout();
				// do NOT call next() -- the auth service will handle the redirect
			},
		},
		...sliceRoutes,
		{
			path: "/.*",
			beforeEnter( to, from, next ) {
				// TODO: 404 page
				next( "/" );
			},
		},
	];

	const router = createRouter( {
		history: createWebHistory( process.env.BASE_URL ),
		routes,
	} );

	router.beforeEach( ( to, from ) => {
		const {
			path: toPath,
			params: toParams,
		} = to;

		const {
			path: fromPath,
			params: fromParams,
			meta: fromMeta = {},
		} = from;

		if ( fromMeta?.bodyClass ) {
			window.document.body.classList.remove( fromMeta.bodyClass );
		}

		//
		// The stream nav sidebar expand/collapse state is persisted across route changes
		// *within* a team/1:1 space, but gets reset if navigating *between* spaces, or
		// if navigating *away* from a team/1:1 space.
		// @see common/team/stores/Stream/index.js:resetStreamStoreState()
		//
		const isTeamSpaceRouteChange = [ "/team/", "/one-on-one/", ]
			.find( path => fromPath.startsWith( path ) && toPath.startsWith( path ) );

		if ( isTeamSpaceRouteChange ) {
			const { teamId: fromTeamId, } = fromParams;
			const { teamId: toTeamId, } = toParams;
			if ( fromTeamId !== toTeamId ) {
				storage.expandStreamNavigationSidebar();
			}
		} else {
			storage.expandStreamNavigationSidebar();
		}
	} );

	router.afterEach( to => {
		setImmediate( () => {
			const userProfile = store.getters[ "host-userProfile/userProfile" ];
			if ( !userProfile?.personId ) {
				return;
			}
			// Re-scope the user on every route change. This is mostly for
			// Intercom, which sends updates that target page elements. Since
			// this is an SPA, it needs to know when a new "page" has been viewed
			// because the elements on the page change.
			telemetry.scopeToUser( userProfile, {
				// eslint-disable-next-line camelcase
				last_request_at: DateTime.now().toUnixInteger(),
			} );
		} );

		setImmediate( () => {
			const meta = to?.meta || {};
			const { title: titleKey = "", preventDefaultRouteTitle = false, bodyClass = "", } = meta;
			// The route meta may define a "title" (i18n key) to be used
			// as the document title. If it does NOT provide a title key,
			// we use the default document title UNLESS the preventDefaultRouteTitle
			// flag is set. If it is, the route's component or layout will
			// be responsible for setting the document title (see: DocumentMixin).
			if ( !titleKey && !preventDefaultRouteTitle ) {
				window.document.title = "Opre";
			} else if ( titleKey ) {
				try {
					const title = i18n.global.t( titleKey );
					window.document.title = i18n.global.t( "teaming-web.routes.teamingRoute", {
						title,
					} );
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.warn( "Missing page title translation key for ", titleKey );
					window.document.title = "Opre";
				}
			}
			// The route meta may define a class to append to the document body.
			// This is used for 3rd party integrations, and potential CSS
			// rules that target elements outside a component (e.g., a 3rd party
			// library creates an element that is a sibling to the app element).
			if ( bodyClass ) {
				window.document.body.classList.add( bodyClass );
			}
		} );

		// Forcibly set the intercom widget to visible, if possible.
		// I can't remember why we do this.
		const { path, } = to;
		setTimeout( () => {
			const intercomLauncher = document.querySelector( ".intercom-launcher" );
			if ( !intercomLauncher ) {
				// eslint-disable-next-line no-console
				return debug( "cannot adjust intercom widget visibility" );
			}
			const hide = intercomWidgetBlacklist.find( prefix => path.startsWith( prefix ) );
			if ( hide ) {
				intercomLauncher.classList.remove( "manifest" );
			} else {
				intercomLauncher.classList.add( "manifest" );
			}
			// eslint-disable-next-line no-magic-numbers
		}, 1000 );
	} );

	return router;
}

export { loadedSlices };
